import { useEffect, useRef, useState, useCallback } from 'react';

// Free public STUN configuration. To support enterprise NAT architectures, replace with high-capacity TURN clusters.
const rtcConfig = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' }
    // { urls: 'turn:your-turn-server.com', username: 'user', credential: 'password' }
  ]
};

export const useWebRTC = (socket, activeChat, setToast) => {
  const [callState, setCallState] = useState({
    status: 'idle', // idle, ringing, incoming, active
    type: null, // voice, video
    targetUser: null,
    callerId: null,
    callerName: null,
    callerAvatar: null
  });

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const peerConnection = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidates = useRef([]);

  const cleanUpCallContext = useCallback(() => {
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
    setRemoteStream(null);
    setCallState({ status: 'idle', type: null, targetUser: null, callerId: null, callerName: null, callerAvatar: null });
    pendingCandidates.current = [];
  }, []);

  const createPeerConnection = useCallback((targetUserId) => {
    const pc = new RTCPeerConnection(rtcConfig);
    peerConnection.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('ice-candidate', { targetUserId, candidate: event.candidate });
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        setToast({ message: 'Dynamic WebRTC media connection matrix lost', type: 'error' });
        cleanUpCallContext();
      }
    };

    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        setRemoteStream(event.streams[0]);
      }
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    return pc;
  }, [socket, cleanUpCallContext, setToast]);

  const initiateCall = useCallback(async (type) => {
    if (!activeChat?.user) return;
    setCallState({ status: 'ringing', type, targetUser: activeChat.user });

    try {
      const constraints = { audio: true, video: type === 'video' };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      setLocalStream(stream);

      const pc = createPeerConnection(activeChat.user._id);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const currentUser = JSON.parse(localStorage.getItem('user'));
      socket.emit('call-user', {
        targetUserId: activeChat.user._id,
        callerId: currentUser.id,
        callerName: currentUser.username,
        callerAvatar: currentUser.avatar,
        signalData: offer,
        type
      });
    } catch (err) {
      setToast({ message: 'Media execution failed. Verify permissions.', type: 'error' });
      cleanUpCallContext();
    }
  }, [activeChat, createPeerConnection, socket, cleanUpCallContext, setToast]);

  const acceptCall = useCallback(async () => {
    if (!callState.callerId || !callState.signalData) return;
    setCallState(prev => ({ ...prev, status: 'active' }));

    try {
      const constraints = { audio: true, video: callState.type === 'video' };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      setLocalStream(stream);

      const pc = createPeerConnection(callState.callerId);
      await pc.setRemoteDescription(new RTCSessionDescription(callState.signalData));
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socket.emit('answer-call', { targetUserId: callState.callerId, signalData: answer });

      while (pendingCandidates.current.length > 0) {
        const candidate = pendingCandidates.current.shift();
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (err) {
      setToast({ message: 'Error initializing callee engine matrix context', type: 'error' });
      cleanUpCallContext();
    }
  }, [callState, createPeerConnection, socket, cleanUpCallContext, setToast]);

  const declineCall = useCallback(() => {
    if (callState.callerId) {
      socket.emit('decline-call', { targetUserId: callState.callerId });
    }
    cleanUpCallContext();
  }, [callState, socket, cleanUpCallContext]);

  const endCall = useCallback(() => {
    const targetId = callState.targetUser?._id || callState.callerId;
    if (targetId) {
      socket.emit('end-call', { targetUserId: targetId });
    }
    cleanUpCallContext();
  }, [callState, socket, cleanUpCallContext]);

  useEffect(() => {
    if (!socket) return;

    socket.on('incoming-call', ({ from, callerName, callerAvatar, signalData, type }) => {
      setCallState({ status: 'incoming', type, callerId: from, callerName, callerAvatar, signalData });
    });

    socket.on('call-accepted', async ({ signalData }) => {
      setCallState(prev => ({ ...prev, status: 'active' }));
      if (peerConnection.current) {
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(signalData));
        while (pendingCandidates.current.length > 0) {
          const candidate = pendingCandidates.current.shift();
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
        }
      }
    });

    socket.on('ice-candidate', async ({ candidate }) => {
      if (peerConnection.current && peerConnection.current.remoteDescription) {
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        pendingCandidates.current.push(candidate);
      }
    });

    socket.on('call-declined', () => {
      setToast({ message: 'The remote call session was declined.', type: 'info' });
      cleanUpCallContext();
    });

    socket.on('call-ended', () => {
      setToast({ message: 'The remote call session has closed.', type: 'info' });
      cleanUpCallContext();
    });

    socket.on('call-error', ({ message }) => {
      setToast({ message, type: 'error' });
      cleanUpCallContext();
    });

    return () => {
      socket.off('incoming-call');
      socket.off('call-accepted');
      socket.off('ice-candidate');
      socket.off('call-declined');
      socket.off('call-ended');
      socket.off('call-error');
    };
  }, [socket, cleanUpCallContext, setToast]);

  return { callState, localStream, remoteStream, initiateCall, acceptCall, declineCall, endCall };
};